/**
 * Organism – ReportList
 * Paginated list of ReportCard components with empty + loading states.
 *
 * Props:
 *  - reports         DailyReport[]
 *  - pagination      { page, limit, total, totalPages }
 *  - onPageChange    (newPage) => void
 *  - isChairman      bool
 *  - onFeedbackSaved (report) => void
 *  - loading         bool
 */
import React from 'react';
import { MdChevronLeft, MdChevronRight, MdInbox } from 'react-icons/md';
import ReportCard from '../molecules/ReportCard';

const ReportList = ({
  reports = [],
  pagination = {},
  onPageChange,
  isChairman = false,
  onFeedbackSaved,
  loading = false
}) => {
  const { page = 1, totalPages = 1, total = 0 } = pagination;

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 rounded-xl bg-background-secondary animate-pulse border border-border-light"
          />
        ))}
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-background-secondary flex items-center justify-center mb-4">
          <MdInbox size={28} className="text-text-tertiary" />
        </div>
        <p className="text-base font-semibold text-text-secondary">No reports found</p>
        <p className="text-sm text-text-tertiary mt-1">
          {isChairman
            ? 'No HR reports match the selected filters.'
            : 'You have not submitted any daily reports yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary line */}
      <p className="text-xs text-text-tertiary">
        Showing {reports.length} of {total} report{total !== 1 ? 's' : ''}
      </p>

      {/* Cards */}
      <div className="space-y-3">
        {reports.map((report) => (
          <ReportCard
            key={report._id}
            report={report}
            isChairman={isChairman}
            onFeedbackSaved={onFeedbackSaved}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => onPageChange && onPageChange(page - 1)}
            disabled={page <= 1}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-border text-text-secondary hover:bg-background disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <MdChevronLeft size={16} /> Previous
          </button>

          <span className="text-sm text-text-secondary">
            Page <span className="font-semibold text-text-primary">{page}</span> of {totalPages}
          </span>

          <button
            onClick={() => onPageChange && onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-border text-text-secondary hover:bg-background disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next <MdChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportList;
